/**
 * F92 — Página global /promesas: histórico filtrable con acciones rápidas.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useCancelPromesa, useListPromesas } from "./api";
import {
  ESTADO_PROMESA_COLOR, ESTADO_PROMESA_LABEL, estadoPromesaEnum, type EstadoPromesa,
} from "./schemas";
import { formatMXN, formatDate } from "@/lib/utils";
import type { ApiError } from "@/lib/api";
// rc139 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

export function PromesasListPage() {
  const [estado, setEstado] = useState<EstadoPromesa | "">("activa");
  const [clienteSearch, setClienteSearch] = useState("");

  const { data, isLoading } = useListPromesas({
    estado: estado || undefined,
    limit: 200,
  });

  const cancelMut = useCancelPromesa();

  // Filtro local por nombre/código de cliente
  const items = (data?.items ?? []).filter((p) => {
    if (!clienteSearch) return true;
    const q = clienteSearch.toLowerCase();
    return (
      p.cliente_nombre?.toLowerCase().includes(q) ||
      p.cliente_codigo?.toLowerCase().includes(q)
    );
  });

  const isMobile = useIsMobile();

  async function cancelar(id: string) {

    const motivo = prompt("Motivo de cancelación (mín. 3 caracteres):");

    if (!motivo || motivo.length < 3) return;

    try {

      await cancelMut.mutateAsync({ id, motivo });

      toast.success("Promesa cancelada");

    } catch (e) {

      toast.error("Error", { description: (e as ApiError).message });

    }

  }


  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <CalendarClock size={16} className="text-amber-600" />
            Promesas de pago
          </h1>
          <p className="text-xs text-muted-foreground max-md:hidden">
            Compromisos de pago activos. Mientras una promesa esté vigente
            el cliente no se suspende automáticamente.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={estado}
          onChange={(e) => setEstado(e.target.value as EstadoPromesa | "")}
          className="w-44 max-md:w-full"
        >
          <option value="">Todos los estados</option>
          {estadoPromesaEnum.map((s) => (
            <option key={s} value={s}>{ESTADO_PROMESA_LABEL[s]}</option>
          ))}
        </Select>
        <Input
          value={clienteSearch}
          onChange={(e) => setClienteSearch(e.target.value)}
          placeholder="Filtrar por cliente o código…"
          className="w-72 max-md:w-full"
        />
        {(estado || clienteSearch) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setEstado(""); setClienteSearch(""); }}
          >
            <RotateCcw size={12} /> Limpiar
          </Button>
        )}
      </div>

      {isMobile ? (
        isLoading && items.length === 0 ? (
          <MobileEmpty>Cargando promesas…</MobileEmpty>
        ) : items.length === 0 ? (
          <MobileEmpty>Sin promesas que mostrar{estado && <> en estado <strong>{ESTADO_PROMESA_LABEL[estado as EstadoPromesa]}</strong></>}.</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((p) => (
              <MobileCard
                key={p.id}
                to={`/clientes/${p.cliente_id}`}
                title={p.cliente_nombre ?? "—"}
                subtitle={<><span className="font-mono">{p.cliente_codigo ?? ""}</span>{p.factura_folio ? <> · <span className="font-mono">{p.factura_folio}</span></> : " · global"}</>}
                right={<Badge className={`text-[9px] ${ESTADO_PROMESA_COLOR[p.estado]}`}>{ESTADO_PROMESA_LABEL[p.estado]}</Badge>}
                actions={
                  p.estado === "activa" ? (
                    <Button size="sm" variant="ghost" className="text-red-600" disabled={cancelMut.isPending} onClick={() => cancelar(p.id)}>
                      <X size={12} /> Cancelar
                    </Button>
                  ) : undefined
                }
              >
                <MobileMeta label="Prometida">
                  {formatDate(p.fecha_prometida)}
                  {p.estado === "activa" && p.dias_restantes !== null && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      {p.dias_restantes >= 0 ? `(en ${p.dias_restantes} d)` : `(vencida hace ${-p.dias_restantes} d)`}
                    </span>
                  )}
                </MobileMeta>
                <MobileMeta label="Monto" mono>{formatMXN(Number(p.monto_prometido))}</MobileMeta>
                <MobileMeta label="Otorgada por">{p.autorizada_por_nombre ?? "—"}{p.reactivacion_disparada && <span className="ml-1 text-emerald-600">· reactivó</span>}</MobileMeta>
                <MobileMeta label="Motivo">{p.motivo ?? "—"}</MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Factura</TableHead>
              <TableHead>Fecha prometida</TableHead>
              <TableHead className="text-right">Monto</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead>Otorgada por</TableHead>
              <TableHead>Motivo</TableHead>
              <TableHead className="w-[110px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando promesas…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                Sin promesas que mostrar
                {estado && <> en estado <strong>{ESTADO_PROMESA_LABEL[estado as EstadoPromesa]}</strong></>}.
              </TableEmpty>
            ) : (
              items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <Link
                      to={`/clientes/${p.cliente_id}`}
                      className="text-sm hover:underline"
                    >
                      {p.cliente_nombre ?? "—"}
                    </Link>
                    {p.cliente_codigo && (
                      <div className="text-[10px] font-mono text-muted-foreground">
                        {p.cliente_codigo}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.factura_folio ? (
                      <span className="font-mono text-xs">{p.factura_folio}</span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">— global —</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(p.fecha_prometida)}
                    {p.estado === "activa" && p.dias_restantes !== null && (
                      <div className="text-[10px] text-muted-foreground">
                        {p.dias_restantes >= 0
                          ? `en ${p.dias_restantes} día(s)`
                          : `vencida hace ${-p.dias_restantes} día(s)`}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatMXN(Number(p.monto_prometido))}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`text-[9px] ${ESTADO_PROMESA_COLOR[p.estado]}`}
                    >
                      {ESTADO_PROMESA_LABEL[p.estado]}
                    </Badge>
                    {p.reactivacion_disparada && (
                      <div className="text-[10px] text-emerald-600">
                        <RotateCcw size={10} className="inline align-text-bottom" /> reactivó
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {p.autorizada_por_nombre ?? "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                    {p.motivo ?? "—"}
                  </TableCell>
                  <TableCell>
                    {p.estado === "activa" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        disabled={cancelMut.isPending}
                        onClick={() => cancelar(p.id)}
                      >
                        <X size={12} /> Cancelar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      )}

      {data && (
        <div className="text-[11px] text-muted-foreground text-right">
          Mostrando {items.length} de {data.total} promesa(s).
        </div>
      )}
    </div>
  );
}
